import { Form, Link } from 'react-router-dom';
import cn from 'classnames';
import { Button, ChevronDownIcon, ExtLinkIcon } from '../../../components';
import { SkipButton } from '../common/SkipButton';
import {
  useIsNoticesAndPostersTask,
  useTaskActionData,
  useTaskRequirement,
  useTaskUrl,
} from '../utils/hooks';
import { TaskHeader } from '../common/TaskHeader';
import { Disclosure } from '@headlessui/react';
import { SubmitButton } from '../common/SubmitButton';
import { Remediation } from '../common/Remediation';
import { FeesAndEta } from '../common/FeesAndEta';
import { TaskActions } from '../common/TaskActions';
import { TaskError } from '../common/TaskError';
import { TaskStatus } from '../utils/types';
import { NextButton } from '../common/NextButton';
import { TaskRequirementNotices } from './TaskRequirementNotices';
import { TaskContainer } from './TaskContainer';

export const TaskRequirement = () => {
  const taskUrl = useTaskUrl();
  const {
    id,
    status,
    source: {
      description,
      resources,
      remediations,
      is_automatable: isAutomatable,
      managed_provider: managedProvider,
      fee,
      eta,
    },
  } = useTaskRequirement();
  const isNotices = useIsNoticesAndPostersTask();
  const actionData = useTaskActionData();
  const isTodo = status === TaskStatus.Todo;

  if (isNotices) {
    return <TaskRequirementNotices />;
  }

  return (
    <TaskContainer>
      <TaskHeader />

      <div>
        <div className={cn({ 'mb-2': actionData?.errors?.submit })}>
          {isTodo && <TaskError>{actionData?.errors?.submit}</TaskError>}
        </div>

        <p>{description}</p>
      </div>

      <ul className="space-y-2">
        {resources.map(({ name, url }) => {
          return (
            <li key={`${name}_${url}`}>
              <a
                href={url}
                target="_blank"
                className="text-teal-700 underline"
                rel="noreferrer"
                aria-label={`${name} – opens in new tab`}
              >
                {name}
                <ExtLinkIcon className="mb-px ml-[0.25em] inline-block size-4" />
              </a>
            </li>
          );
        })}
      </ul>

      <article className="mt-2 space-y-6">
        <header>
          <h2 className="text-2xl font-bold">
            {remediations.length > 1
              ? isTodo
                ? isAutomatable
                  ? 'You Can Automate These Steps'
                  : 'What You Need to Do'
                : managedProvider
                  ? "You've Automated These Steps"
                  : "You've Marked These Steps as Done"
              : remediations[0].title}
          </h2>

          {isTodo && isAutomatable && remediations.length > 1 && (
            <p>
              Alternatively, you can do each step manually and then mark this
              task as done
            </p>
          )}
        </header>

        {remediations.length > 1 ? (
          <ol>
            {remediations.map((remediation) => {
              return (
                <Disclosure
                  as="li"
                  key={remediation.id}
                  className="group rounded hover:bg-newTeal-100"
                >
                  <Disclosure.Button className="flex w-full gap-x-1 py-1 pl-2 font-medium text-newTeal-800 group-hover:text-newTeal-900">
                    <ChevronDownIcon className="mt-1.5 size-4 shrink-0 -rotate-90 transition-transform ui-open:rotate-0" />
                    <span className="text-left leading-7">
                      {remediation.title}
                    </span>
                  </Disclosure.Button>

                  <Disclosure.Panel className="pb-3 pl-7 pr-4 pt-1">
                    <Remediation remediation={remediation} />
                  </Disclosure.Panel>
                </Disclosure>
              );
            })}
          </ol>
        ) : (
          <Remediation remediation={remediations[0]} />
        )}
      </article>

      <FeesAndEta fee={fee} eta={eta} />

      <TaskActions>
        {!isTodo ? (
          <NextButton>Next</NextButton>
        ) : (
          <Form
            method="POST"
            className="flex items-center justify-end gap-x-6"
            action={taskUrl}
          >
            <SkipButton />

            <SubmitButton
              key={`mark-as-done-button-${id}`}
              variant={isAutomatable ? 'secondary' : 'primary'}
              intent="mark-as-done"
            >
              I&apos;ve Completed This
            </SubmitButton>

            {isAutomatable && (
              <Button as={Link} to="automation" size="xlarge">
                Automate
              </Button>
            )}
          </Form>
        )}
      </TaskActions>
    </TaskContainer>
  );
};
